import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp, ChevronLeft, ChevronRight, AlertCircle, Loader } from 'lucide-react';
import AdminLayout from '@/components/layout/AdminLayout';
import apiService from '@/services/api';
import AssignProviderModal from '@/components/modals/AssignProviderModal';

interface Job {
  id: string;
  customer: string;
  property: string;
  service: string;
  provider: string;
  status: 'Overdue' | 'Variation Pending' | 'In Progress' | 'Awaiting Assignment' | 'Quality Check';
  risk: 'High' | 'Medium' | 'Low' | null;
  description: string;
  timeline: Array<{
    label: string;
    date: string;
    time: string;
  }>;
}
const OrdersJobsPage = () => {
  const navigate = useNavigate();
  const [currentFilter, setCurrentFilter] = useState('All');
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterCounts, setFilterCounts] = useState({
    all: 0,
    awaiting_assignment: 0,
    in_progress: 0,
    quality_check: 0,
    overdue: 0
  });
  const [assignmentModalJobId, setAssignmentModalJobId] = useState<string | null>(null);

  // Load jobs from API
  const loadJobs = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Build query parameters
      const params = new URLSearchParams();
      if (currentFilter !== 'All') {
        params.append('status', currentFilter);
      }
      params.append('page', String(currentPage));
      
      const response = await apiService.get(`/admin/orders-jobs?${params.toString()}`);
      
      if (response?.data) {
        setJobs(response.data);
        if (response.filters) {
          setFilterCounts(response.filters);
        }
      }
    } catch (err) {
      console.error('Error fetching jobs:', err);
      setError('Failed to load jobs. Please try again.');
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  // Load jobs on component mount and when filter/page changes
  useEffect(() => {
    loadJobs();
  }, [currentFilter, currentPage]);

  const handleAssignProvider = async (providerId: string, notes: string, notifications: { app: boolean; sms: boolean }) => {
    if (!assignmentModalJobId) return;
    
    try {
      const response = await apiService.post(`/admin/jobs/${assignmentModalJobId}/assign-provider`, {
        provider_id: providerId,
        notes,
        notify_via: notifications
      });

      if (response?.success) {
        // Refresh jobs list
        await loadJobs();
        setAssignmentModalJobId(null);
      } else {
        console.error('Failed to assign provider:', response?.message);
      }
    } catch (err) {
      console.error('Error assigning provider:', err);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Overdue':
        return 'bg-red-100 text-red-700';
      case 'Variation Pending':
        return 'bg-yellow-100 text-yellow-700';
      case 'In Progress':
        return 'bg-blue-100 text-blue-700';
      case 'Awaiting Assignment':
        return 'bg-gray-100 text-gray-700';
      case 'Quality Check':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getRiskColor = (risk: string | null) => {
    if (!risk) return '';
    switch (risk) {
      case 'High':
        return 'text-red-600';
      case 'Medium':
        return 'text-yellow-600';
      case 'Low':
        return 'text-green-600';
      default:
        return '';
    }
  };

  const filteredJobs = jobs;

  return (
    <>
      <Helmet>
        <title>Orders & Jobs - Virewire</title>
      </Helmet>

      <AdminLayout>
        <div className="min-h-screen bg-gray-50 p-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Orders & Jobs</h1>
            <p className="text-gray-600">Manage and orchestrate all service jobs on the platform.</p>
          </div>

          {/* Filter Tabs */}
          <div className="flex gap-3 mb-8 flex-wrap">
            {[
              { label: 'All', key: 'all', count: filterCounts.all },
              { label: 'Awaiting Assignment', key: 'awaiting_assignment', count: filterCounts.awaiting_assignment },
              { label: 'In Progress', key: 'in_progress', count: filterCounts.in_progress },
              { label: 'Quality Check', key: 'quality_check', count: filterCounts.quality_check },
              { label: 'Overdue', key: 'overdue', count: filterCounts.overdue },
            ].map(tab => (
              <button
                key={tab.label}
                onClick={() => {
                  setCurrentFilter(tab.label);
                  setCurrentPage(1);
                }}
                className={`px-4 py-2 rounded-full font-medium text-sm transition-colors ${
                  currentFilter === tab.label
                    ? 'bg-teal-700 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {tab.label} <span className="ml-2">{tab.count}</span>
              </button>
            ))}
          </div>

          {/* Error State */}
          {error && (
            <div className="mb-8 bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm font-semibold text-red-900">⚠ Error loading jobs</p>
              <p className="text-xs text-red-700 mt-1">{error}</p>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden p-8 text-center">
              <div className="flex items-center justify-center gap-2">
                <Loader className="w-5 h-5 text-teal-700 animate-spin" />
                <p className="text-gray-600">Loading jobs...</p>
              </div>
            </div>
          )}

          {/* Empty State */}
          {!loading && filteredJobs.length === 0 && (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden p-8 text-center">
              <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 font-medium">No jobs found</p>
              <p className="text-gray-500 text-sm mt-1">No jobs matching the current filter</p>
            </div>
          )}

          {/* Table */}
          {!loading && filteredJobs.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-24"></th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Job ID</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Customer</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Service</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Provider</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Risk</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-12"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredJobs.map((job, index) => (
                    <React.Fragment key={`row-${job.id}-${index}`}>
                      <tr 
                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => setExpandedJobId(expandedJobId === job.id ? null : job.id)}
                      >
                        <td className="px-6 py-4">
                          <div className="w-1 h-8 bg-red-500 rounded-full"></div>
                        </td>
                        <td className="px-6 py-4 font-semibold text-gray-900">{job.id}</td>
                        <td className="px-6 py-4 text-gray-700">{job.customer}</td>
                        <td className="px-6 py-4 text-gray-700">{job.service}</td>
                        <td className="px-6 py-4 text-gray-700">{job.provider}</td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(job.status)}`}>
                            ● {job.status}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {job.risk ? (
                            <div className="flex items-center gap-1">
                              <AlertCircle className={`w-4 h-4 ${getRiskColor(job.risk)}`} />
                              <span className={`text-sm font-medium ${getRiskColor(job.risk)}`}>
                                {job.risk === 'High' && '!'}
                                {job.risk === 'Medium' && '⚠'}
                              </span>
                            </div>
                          ) : null}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
                            {expandedJobId === job.id ? (
                              <ChevronUp className="w-5 h-5 text-gray-400" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-gray-400" />
                            )}
                          </button>
                        </td>
                      </tr>

                      {/* Expanded Detail Row */}
                      {expandedJobId === job.id && (
                        <tr key={`detail-${job.id}`} className="bg-gray-50 border-t border-gray-200">
                          <td colSpan={9} className="px-6 py-8">
                            <div className="grid grid-cols-3 gap-8">
                              {/* Left Column - Job Description */}
                              <div>
                                <h3 className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-4">Job Description</h3>
                                <p className="text-sm text-gray-700 leading-relaxed">{job.description}</p>
                              </div>

                              {/* Middle Column - Operational Timeline */}
                              <div>
                                <h3 className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-4">Operational Timeline</h3>
                                <div className="space-y-4">
                                  {job.timeline.map((event, idx) => (
                                    <div key={idx} className="flex gap-3">
                                      <div className="flex flex-col items-center">
                                        <div className="w-3 h-3 bg-teal-500 rounded-full"></div>
                                        {idx < job.timeline.length - 1 && (
                                          <div className="w-0.5 h-10 bg-gray-300 my-1"></div>
                                        )}
                                      </div>
                                      <div>
                                        <p className="text-sm font-semibold text-gray-900">{event.label}</p>
                                        {event.time && (
                                          <p className="text-xs text-gray-600">{event.date} • {event.time}</p>
                                        )}
                                        {!event.time && (
                                          <p className="text-xs text-gray-600">{event.date}</p>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {/* Right Column - Actions */}
                              <div className="flex flex-col justify-between">
                                <div className="space-y-3">
                                  {job.status === 'Awaiting Assignment' && (
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                      <p className="text-sm font-semibold text-blue-900">👤 Assign Provider</p>
                                      <p className="text-xs text-blue-700 mt-1">No provider assigned yet</p>
                                      <Button
                                        onClick={() => setAssignmentModalJobId(job.id)}
                                        className="w-full bg-teal-700 hover:bg-teal-800 text-white mt-3"
                                      >
                                        Select Provider
                                      </Button>
                                    </div>
                                  )}
                                  {job.status === 'Variation Pending' && (
                                    <>
                                      <Button 
                                        variant="outline" 
                                        className="w-full text-red-600 border-red-600 hover:bg-red-50"
                                      >
                                        Reject Variation
                                      </Button>
                                      <Button 
                                        className="w-full bg-teal-700 hover:bg-teal-800 text-white"
                                      >
                                        Approve Variation (£450)
                                      </Button>
                                    </>
                                  )}
                                  {job.status === 'Quality Check' && (
                                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                      <p className="text-sm font-semibold text-green-900">✓ Quality check in progress</p>
                                      <p className="text-xs text-green-700 mt-1">Awaiting quality verification</p>
                                    </div>
                                  )}
                                  {job.status === 'Overdue' && (
                                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                      <p className="text-sm font-semibold text-red-900">! Action Required</p>
                                      <p className="text-xs text-red-700 mt-1">This job is overdue and needs attention</p>
                                    </div>
                                  )}
                                </div>
                                <Button
                                  onClick={() => navigate(`/admin/job/${job.id}`)}
                                  className="w-full bg-gray-900 hover:bg-gray-800 text-white mt-4"
                                >
                                  View Details →
                                </Button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          )}

          {/* Pagination */}
          {!loading && filteredJobs.length > 0 && (
          <div className="flex items-center justify-between mt-6">
            <p className="text-sm text-gray-600">
              Showing {filteredJobs.length} jobs
            </p>
            <div className="flex items-center gap-2">
              <button className="p-2 hover:bg-gray-200 rounded-lg disabled:opacity-50" disabled={currentPage === 1}>
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button className="px-3 py-2 bg-teal-700 text-white rounded-lg font-medium">
                {currentPage}
              </button>
              <button className="p-2 hover:bg-gray-200 rounded-lg">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
          )}

          {/* Assignment Modal */}
          {assignmentModalJobId && (
            <AssignProviderModal
              jobId={assignmentModalJobId}
              serviceType={jobs.find(j => j.id === assignmentModalJobId)?.service || ''}
              location={jobs.find(j => j.id === assignmentModalJobId)?.property || ''}
              scheduledTime=""
              priority="Medium"
              isOpen={true}
              onClose={() => setAssignmentModalJobId(null)}
              onAssign={handleAssignProvider}
            />
          )}
        </div>
      </AdminLayout>
    </>
  );
};

export default OrdersJobsPage;
